/**
 * Safe key/value storage on top of `localStorage`.
 *
 * Every access is wrapped in try/catch: storage may be disabled (privacy mode, blocked cookies),
 * full, or throw on access in sandboxed iframes. When it fails we fall back to an in-memory map so
 * the app keeps working for the lifetime of the page.
 */
const memory = new Map<string, string>();

function backend(): Storage | null {
  try {
    const s = globalThis.localStorage;
    return s ?? null;
  } catch {
    return null;
  }
}

export function readItem(key: string): string | null {
  try {
    const s = backend();
    if (s) {
      const v = s.getItem(key);
      if (v !== null) return v;
    }
  } catch {
    // fall through to memory
  }
  return memory.get(key) ?? null;
}

export function writeItem(key: string, value: string): void {
  memory.set(key, value);
  try {
    backend()?.setItem(key, value);
  } catch {
    // quota exceeded / blocked: the in-memory copy is enough for this page
  }
}

export function removeItem(key: string): void {
  memory.delete(key);
  try {
    backend()?.removeItem(key);
  } catch {
    // ignore
  }
}

export function readJson(key: string): unknown {
  const raw = readItem(key);
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

export function writeJson(key: string, value: unknown): void {
  writeItem(key, JSON.stringify(value));
}

/** Test helper: forget the in-memory fallback. */
export function _clearMemoryStorage(): void {
  memory.clear();
}
